import { RefObject, useEffect } from 'react';
import { EdgeDocsCaret } from '../../EdgeDocsCaret';
import { SelectionRenderer } from '../selection';
import { InputLogger } from '../../../../../../utils/loggers/InputLogger';

export interface CaretAndSelectionInitDependencies {
  overlayCanvasRef: RefObject<HTMLCanvasElement | null>;
  caretRef: RefObject<EdgeDocsCaret | null>;
  selectionRendererRef: RefObject<SelectionRenderer | null>;
  drawCaretForBlink: () => void;
}

/**
 * Initializes caret overlay and selection renderer with proper lifecycle management.
 * Sets up caret instance with smart redraw patching and selection renderer.
 */
// 🔴 DEBUG: Track effect run count to prove if it's re-running
let caretInitEffectRunCount = 0;

export function setupCaretAndSelectionRenderer({
  overlayCanvasRef,
  caretRef,
  selectionRendererRef,
  drawCaretForBlink
}: CaretAndSelectionInitDependencies) {
  return useEffect(() => {
    caretInitEffectRunCount++;
    InputLogger.caretDiag.useEffectRun(caretInitEffectRunCount, 'drawCaretForBlink dependency changed');
    
    const overlay = overlayCanvasRef.current;
    if (!overlay) return;

    InputLogger.caretDiag.overlayInit();

    // Create caret instance but DON'T show it yet
    // We'll show it after we have valid WASM data and can position it correctly
    if (!caretRef.current) {
      caretRef.current = new EdgeDocsCaret(overlay);
      InputLogger.caretDiag.instanceCreated(true);

      // ✅ CRITICAL: Patch the caret's redraw method to use our smart rendering
      const caret = caretRef.current as any;
      const originalRedraw = caret.redraw.bind(caret);

      caret.redraw = () => {
        // When the caret calls its own redraw (during blinks), use our smart clearing
        // 🔧 FIX (2026-01-23): Remove requestAnimationFrame wrapper to prevent ghost caret
        // during fast typing. The rAF caused a race condition where:
        // 1. Animation calls redraw() which scheduled drawCaretForBlink for next frame
        // 2. Animation also scheduled itself for next frame
        // 3. Position changed between scheduling and execution, causing stale clearing
        if (caret.isActive) {
          drawCaretForBlink();
        } else {
          // If not active, just call original (which clears)
          originalRedraw();
        }
      };

      InputLogger.caretDiag.redrawPatched();
    } else {
      InputLogger.caretDiag.instanceCreated(false, { alreadyExists: true });
    }

    // ✅ Initialize selection renderer
    const ctx = overlay.getContext('2d');
    if (ctx && !selectionRendererRef.current) {
      selectionRendererRef.current = new SelectionRenderer({
        ctx,
        color: '#0078D4', // MS Word blue
        opacity: 0.3
      });
      InputLogger.caretDiag.selectionRendererCreated();
    }

    return () => {
      // Cleanup
      InputLogger.caretDiag.cleanupDispose(caretInitEffectRunCount);
      if (caretRef.current) {
        caretRef.current.dispose();
        // 🔴 CRITICAL: Set ref to null so next effect run creates a new one
        // This was missing! The dispose() doesn't null the ref, so the next run
        // sees caretRef.current as truthy and skips creation
        caretRef.current = null;
      }
      selectionRendererRef.current = null;
    };
  }, [drawCaretForBlink]);
}
